import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { UpdateOne_stage_Data } from '../../Redux/Actions/Stage_Action';
import { Notify } from '../../Utility/Notify';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconWarning = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ─── Component ──────────────────────────────────────────────────────────── */
export const StagesActions = ({ stage, allstages }) => {
  const dispatch = useDispatch();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';

  const [show,    setShow]    = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await dispatch(UpdateOne_stage_Data(stage._id, { stageisactivate: 'false' }));
      Notify('success', 'Deleted successfully', 'success');
      setShow(false);
      allstages();
    } catch {
      Notify('error', 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── shared button style ── */
  const actionBtn = (color) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '30px', height: '30px', borderRadius: '8px', border: 'none',
    cursor: 'pointer', transition: 'all 0.15s', background: 'transparent',
    color, flexShrink: 0,
  });

  return (
    <>
      {/* ─── Action Buttons ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Link to={`/updatestagesAdmin/${stage._id}`} style={{ textDecoration: 'none' }}>
          <button
            style={actionBtn(isDark ? '#93c5fd' : '#2563eb')}
            title="Edit Stage"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(99,102,241,0.18)' : 'rgba(37,99,235,0.09)';
              e.currentTarget.style.borderRadius = '8px';
            }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconEdit />
          </button>
        </Link>

        <button
          style={actionBtn(isDark ? '#fca5a5' : '#ef4444')}
          title="Delete Stage"
          onClick={() => setShow(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.09)';
          }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <IconTrash />
        </button>
      </div>

      {/* ─── Delete Confirm Modal ────────────────────────────────────── */}
      {show && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShow(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 40000,
            backgroundColor: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            animation: 'st-fade-in 0.18s ease',
          }}
        >
          <style>{`
            @keyframes st-fade-in  { from { opacity: 0; }             to { opacity: 1; } }
            @keyframes st-slide-up { from { transform: translateY(16px) scale(0.97); opacity: 0; }
                                     to   { transform: translateY(0)    scale(1);    opacity: 1; } }
          `}</style>

          <div style={{
            width: '100%', maxWidth: '420px', borderRadius: '20px', overflow: 'hidden',
            backgroundColor: isDark ? '#1a1d2e' : '#ffffff',
            border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
            boxShadow: isDark
              ? '0 24px 60px rgba(0,0,0,0.6)'
              : '0 20px 60px rgba(0,0,0,0.15)',
            animation: 'st-slide-up 0.22s cubic-bezier(0.34,1.56,0.64,1)',
            fontFamily: "'DM Sans','Segoe UI',sans-serif",
          }}>

            {/* Header */}
            <div style={{
              padding: '18px 20px 16px',
              borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f3f8'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em',
                textTransform: 'uppercase', color: isDark ? '#4b5563' : '#94a3b8',
              }}>
                Confirm Action
              </span>
              <button
                onClick={() => setShow(false)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '28px', height: '28px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  color: isDark ? '#9ca3af' : '#6b7280', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
              >
                <IconX />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '28px 24px 24px', textAlign: 'center' }}>

              {/* Warning Icon */}
              <div style={{
                width: '64px', height: '64px', borderRadius: '18px', margin: '0 auto 20px',
                background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.18)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isDark ? '#fca5a5' : '#ef4444',
              }}>
                <IconWarning />
              </div>

              {/* Stage Name Badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '20px', marginBottom: '14px',
                background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
                fontSize: '13px', fontWeight: 700,
                color: isDark ? '#e5e7eb' : '#111827',
              }}>
                <span style={{
                  width: '20px', height: '20px', borderRadius: '6px', fontSize: '10px', fontWeight: 800,
                  background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {stage.name?.slice(0, 2).toUpperCase() || 'ST'}
                </span>
                {stage.name}
              </div>

              <h3 style={{
                fontSize: '17px', fontWeight: 800, margin: '0 0 8px',
                color: isDark ? '#f9fafb' : '#111827', letterSpacing: '-0.3px',
              }}>
                Delete this stage?
              </h3>
              <p style={{
                fontSize: '13px', color: isDark ? '#6b7280' : '#9ca3af',
                margin: '0 0 28px', lineHeight: '1.6',
              }}>
                This stage will be deactivated and removed from the pipeline.
                This action cannot be undone.
              </p>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShow(false)}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: '12px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                    fontFamily: "'DM Sans',sans-serif",
                    background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                    color: isDark ? '#d1d5db' : '#374151',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: '12px', border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 700,
                    fontFamily: "'DM Sans',sans-serif",
                    background: loading
                      ? (isDark ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.5)')
                      : 'linear-gradient(135deg,#ef4444,#dc2626)',
                    color: '#fff', transition: 'all 0.2s',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(239,68,68,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: loading ? 0.75 : 1,
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {loading ? (
                    <>
                      <span style={{
                        width: '14px', height: '14px', borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                        animation: 'st-spin 0.7s linear infinite', display: 'inline-block',
                      }} />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <IconTrash />
                      Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
